/**
 * Plan — the canonical domain model for subscription tiers.
 *
 * One frozen singleton per tier; `Plan.from(raw)` maps any wire value (or
 * garbage) to a singleton and never throws. Business rules live here as
 * predicate methods, mirroring backend-core-v2's `SubscriptionType`
 * (src/serializers/enums.py) so rules are greppable across repos.
 *
 * TWO HARD CONVENTIONS (enforce in review):
 *   1. Never put a Plan instance into the Redux store — raw wire strings stay
 *      in state; construct the Plan at the read boundary via `selectPlan`.
 *   2. Never spread an instance (`{...plan}`) — spreading silently drops the
 *      methods. Pass the instance itself, or pass `plan.tier` and re-derive.
 *
 * Why singletons: `Plan.from` returns the same instance for the same tier, so
 * `useSelector(selectPlan)` is referentially stable across renders with no
 * memoization machinery — the reference only changes when the tier changes.
 *
 * Why rank −1 for ANONYMOUS: a null-object anonymous plan means no call site
 * needs null checks, and every comparison predicate is safe by construction
 * (anonymous is never paid, never purchasable, never an upgrade target).
 * Anonymous ≠ free: anonymous users route to signup, not checkout (see
 * tierActions.resolveConsultationAction).
 *
 * Spec: openspec/changes/domain-plan-model/specs/plan-domain-model/spec.md
 * Roadmap: docs/domain-refactor-roadmap.md (Modules 1-2)
 */
import { TIER_DESCRIPTORS } from "./descriptors";
import { SUBSCRIPTION_TIERS } from "./tiers";
import type { SubscriptionTier, TierRank } from "./tiers";

/** ANONYMOUS −1, free 0 (both free tiers), then the three paid ranks. */
export type PlanRank = -1 | TierRank;

/**
 * The only tier fact that is not display data and so does not live in a
 * descriptor. `inhouse_counsel` is retired from sale per Pricing 3.5 /
 * INH-5059; existing subscribers keep it, so it is paid but not purchasable.
 */
const PURCHASABLE_TIERS: ReadonlySet<SubscriptionTier> = new Set<SubscriptionTier>([
  "subscriber_business",
  "subscriber_enterprise",
]);

interface PlanInit {
  tier: SubscriptionTier | null;
  rank: PlanRank;
  displayName: string;
  monthlyPriceCents: number;
  includedConsultations: number;
  purchasable: boolean;
}

export class Plan {
  /** Wire value, or null for the anonymous null object. */
  readonly tier: SubscriptionTier | null;
  /** Ordering for upgrade/downgrade comparison. ANONYMOUS −1, free 0, then 1..3. */
  readonly rank: PlanRank;
  /** Current public name ("Inhouse Counsel Plus" lives here and nowhere else). */
  readonly displayName: string;
  readonly monthlyPriceCents: number;
  /** Consultations included per billing cycle (descriptor `monthlyTasks`). */
  readonly includedConsultations: number;

  private readonly purchasable: boolean;

  private constructor(init: PlanInit) {
    this.tier = init.tier;
    this.rank = init.rank;
    this.displayName = init.displayName;
    this.monthlyPriceCents = init.monthlyPriceCents;
    this.includedConsultations = init.includedConsultations;
    this.purchasable = init.purchasable;
    Object.freeze(this);
  }

  /** Null object for missing/unknown subscription_type. */
  static readonly ANONYMOUS = new Plan({
    tier: null,
    rank: -1,
    displayName: "Anonymous",
    monthlyPriceCents: 0,
    includedConsultations: 0,
    purchasable: false,
  });

  /** One singleton per wire tier, built from the matching descriptor. */
  private static readonly REGISTRY: Record<SubscriptionTier, Plan> = Object.fromEntries(
    SUBSCRIPTION_TIERS.map((tier) => {
      const descriptor = TIER_DESCRIPTORS[tier];
      return [
        tier,
        new Plan({
          tier,
          rank: descriptor.rank,
          displayName: descriptor.name,
          monthlyPriceCents: descriptor.monthlyPriceCents,
          includedConsultations: descriptor.monthlyTasks,
          purchasable: PURCHASABLE_TIERS.has(tier),
        }),
      ];
    }),
  ) as Record<SubscriptionTier, Plan>;

  private static readonly ALL: readonly Plan[] = Object.freeze([
    Plan.ANONYMOUS,
    ...Object.values(Plan.REGISTRY),
  ]);

  /** Named constructor: any raw value → singleton. Unknown/missing → ANONYMOUS. Never throws. */
  static from(raw: string | null | undefined): Plan {
    if (!raw) return Plan.ANONYMOUS;
    return Plan.REGISTRY[raw as SubscriptionTier] ?? Plan.ANONYMOUS;
  }

  isAnonymous(): boolean {
    return this.tier === null;
  }

  /** free_tier AND pre_onboarding — per subscription-tier-model, both are Free at every gate. */
  isFree(): boolean {
    return this.tier === "free_tier" || this.tier === "pre_onboarding";
  }

  isPaid(): boolean {
    return this.rank >= 1;
  }

  /** Can this plan be bought today? ≠ isPaid(): the $499 tier is paid but retired. */
  isPurchasable(): boolean {
    return this.purchasable;
  }

  /** Counsel or above (rank ≥ 2). Controls privilege banners, privacy capture, AI selector gating. */
  isPrivilegedChatUser(): boolean {
    return this.rank >= 2;
  }

  /** Mirrors backend SubscriptionType.allows_consultation(). */
  allowsConsultation(): boolean {
    return this.includedConsultations > 0;
  }

  /**
   * Derived, never tabulated: true iff some purchasable plan outranks this
   * one. Encodes INH-5059 ("Counsel is the top purchasable tier") in one
   * place — if the $499 tier ever returns to sale, adding it to
   * `PURCHASABLE_TIERS` restores every upgrade path at once.
   */
  hasUpgradePath(): boolean {
    return Plan.ALL.some((plan) => plan.purchasable && plan.rank > this.rank);
  }

  /** Moving from this plan to `other` is an upgrade. */
  isUpgradeTo(other: Plan): boolean {
    return other.rank > this.rank;
  }

  /** Moving from this plan to `other` is a downgrade. */
  isDowngradeTo(other: Plan): boolean {
    return other.rank < this.rank;
  }

  isRank(rank: PlanRank): boolean {
    return this.rank === rank;
  }
}
